// HER DASHBOARD ITEM, SURFACE 2(a) — **THE LANDING FACTS, READ ONCE, AT MODULE LOAD.**
//
// >>> THE TIMING IS THE WHOLE OF IT, AND IT IS WHY THIS IS A MODULE RATHER THAN TWO LINES IN AN
// EFFECT. <<< `document.referrer` names the page that sent this browser HERE, and an SPA navigation
// does not reload the document — so the value is still the Instagram post four rooms later, and it
// is still correct. Read inside the ping effect instead, it is re-read on every route change, and on
// some browsers that is this site: **a visitor she PAID for would be filed as `direct` from her
// second page onwards, and the campaign would read as organic.** The same holds for the tag, because
// `goTo` rewrites the address and `utm_source` is gone by the second ping.
//
// Both are handed to the SERVER as they are and resolved there against the shared roster in
// `src/traffic.js`, so nothing unbounded is ever stored and the client holds no opinion about what a
// source is (R28/R29).
//
// It imports NOTHING, which is `src/lib`'s own entry requirement and what lets a `.mjs` gate load
// and RUN it — the reason it lives here rather than in the twelve-thousand-line file that used to
// hold it, where the one fact that decides every attribution on this site could only ever be grepped.
//
// **THE `typeof` GUARDS ARE NOT DEFENSIVENESS.** This module is imported by a gate that runs in Node,
// where there is no `document` and no `window`; without them the import throws and the fixture cannot
// reach a line of it.

// The page that sent this browser here, exactly as the browser reports it. "" when nobody did, which
// the shared law reads as `direct`.
export const landingReferrer = (): string =>
  (typeof document === "undefined" ? "" : String(document.referrer || ""));

// The tag an ad platform put on its own link. It OUTRANKS the referrer at the door, because an ad
// click usually arrives through a redirector or with no referrer at all.
export const landingUtm = (): string =>
  (typeof window === "undefined"
    ? ""
    : String(new URLSearchParams(window.location.search).get("utm_source") || ""));

// Read ONCE, here, at module load — before any navigation can move either of them.
export const LANDING_REFERRER = landingReferrer();
export const LANDING_UTM = landingUtm();

// F1/F2 — the funnel key, read from the address and handed to both the page and the capture, so the
// headline and the lead's source column can never disagree about which ad this visitor came through.
// **It moved here from App.tsx and the reason its old comment gave for living there still holds:**
// it is not in `src/funnels.js` because that law is shared with the SERVER, where there is no window
// at all. This module is the client's one reader of the address, which is what it now is — three
// address reads, one place, none of them a second opinion about another (R28/R29). It stays a
// FUNCTION rather than a constant, because the router asks it at render and F1's own page may be
// arrived at more than once in a session.
export const funnelParam = (): string | null =>
  (typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("f"));

// SURFACE 2(b) — **WHICH DOOR THIS BROWSER LANDED ON, READ ONCE, FOR THE SAME REASON THE TAG IS.**
//
// The ping fires on every route change and `goTo` rewrites the address, so a ping that re-read `f`
// at send time would find it gone — and her funnel visits would be counted for the first page of a
// session and nowhere after it, which on a page that exists to receive one landing is the difference
// between a denominator and a rumour. Read here, at module load, before any navigation can move it.
//
// It is the RAW parameter and not a resolved key: **the resolution is `funnelFromParam`'s and it
// happens on the SERVER**, where the roster lives and where the row is written, so the client holds
// no opinion about which doors exist (R28/R29 — the same split the referrer and the tag already
// have, and the reason nothing unbounded is ever stored).
export const LANDING_FUNNEL = funnelParam() || "";
